export type PresentedFrame = {
  readonly mechanism: 'rvfc' | 'timeupdate';
  readonly currentTime: number;
};

type PresentedFrameListener = (frame: PresentedFrame) => void;

type VideoFrameCallbackMetadata = { readonly mediaTime?: number };

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    callback: (now: number, metadata: VideoFrameCallbackMetadata) => void
  ) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/** `readyState` floor at which the element holds data for the current position. */
const HAVE_CURRENT_DATA = 2;

/**
 * A `timeupdate` is only evidence of decoded video when the element is
 * genuinely playing its current data. An empty, paused or seeking element fires
 * `timeupdate` too (seek scrubbing, `currentTime` assignment), and that is not
 * a presented frame.
 */
const isPlayingCurrentData = (element: HTMLVideoElement): boolean =>
  element.readyState >= HAVE_CURRENT_DATA && !element.paused && !element.ended && !element.seeking;

/**
 * Observe decoded/presented progress on the real video element.
 *
 * rVFC is primary and reports every decoded frame, black ones included —
 * brightness is never inspected. The `timeupdate` fallback exists for browsers
 * without rVFC and counts only genuine media-time advancement from the baseline
 * captured when observation started.
 */
export const observePresentedFrames = (
  element: HTMLVideoElement,
  onPresentedFrame: PresentedFrameListener
): (() => void) => {
  const video = element as FrameCallbackVideo;
  let stopped = false;

  if (typeof video.requestVideoFrameCallback === 'function') {
    let handle = 0;
    const tick = (_now: number, metadata: VideoFrameCallbackMetadata): void => {
      // A callback already queued when cleanup ran must neither publish nor
      // rearm; the browser cannot recall it, so the flag is the only fence.
      if (stopped) {
        return;
      }
      onPresentedFrame({
        mechanism: 'rvfc',
        currentTime: metadata.mediaTime ?? element.currentTime
      });
      if (!stopped) handle = video.requestVideoFrameCallback?.(tick) ?? 0;
    };
    handle = video.requestVideoFrameCallback(tick);
    return () => {
      stopped = true;
      video.cancelVideoFrameCallback?.(handle);
    };
  }

  let lastTime = Number.isFinite(element.currentTime) ? element.currentTime : 0;
  const onTimeUpdate = (): void => {
    if (stopped) return;
    const currentTime = element.currentTime;
    if (!isPlayingCurrentData(element)) {
      // A seek/pause changes position without proving playback.
      lastTime = currentTime;
      return;
    }
    if (!Number.isFinite(currentTime) || currentTime <= lastTime) {
      return;
    }
    lastTime = currentTime;
    onPresentedFrame({ mechanism: 'timeupdate', currentTime });
  };

  const resetBaseline = (): void => {
    lastTime = element.currentTime;
  };
  element.addEventListener('seeking', resetBaseline);
  element.addEventListener('seeked', resetBaseline);
  element.addEventListener('timeupdate', onTimeUpdate);
  return () => {
    stopped = true;
    element.removeEventListener('seeking', resetBaseline);
    element.removeEventListener('seeked', resetBaseline);
    element.removeEventListener('timeupdate', onTimeUpdate);
  };
};
